import type { MaxHrStep } from '@painpal/contracts';
import { Check, Link2 } from 'lucide-react';
import { useId, useState } from 'react';
import {
  useAcceptFtpSuggestion, useAcceptMaxHrSteps, useDismissFtpSuggestion, useDismissMaxHrSuggestion, useStravaConnect,
} from '../../api/mutations';
import { useThresholdSuggestions } from '../../api/queries';
import { errorMessage } from '../../lib/api';
import { formatDayMonthYear } from '../../lib/format';
import { acceptStepsLabel, toggled } from './thresholdModel';
import styles from './suggestions.module.css';

function ActionError({ error }: { error: Error | null }) {
  return error === null ? null : <p role="alert" className={`alert-hot ${styles.error}`}>{errorMessage(error)}</p>;
}

function StravaReconnect() {
  const connect = useStravaConnect();
  return (
    <div className={styles.reconnect}>
      <Link2 size={14} aria-hidden="true" className={styles.reconnectIcon} />
      <p className={styles.reconnectText}>Reconnect Strava to read your FTP</p>
      <button type="button" className={`btn btn-outline ${styles.action}`} disabled={connect.isPending} onClick={() => connect.mutate({ returnTo: '/settings' })}>
        RECONNECT
      </button>
      <ActionError error={connect.error} />
    </div>
  );
}

function StravaFtpOffer({ strava, yours }: { strava: number; yours: number | null }) {
  const accept = useAcceptFtpSuggestion();
  const dismiss = useDismissFtpSuggestion();
  const busy = accept.isPending || dismiss.isPending;
  return (
    <div className={styles.ftpOffer} role="group" aria-label="Strava FTP suggestion">
      <p className={styles.values}>
        <span className={styles.label}>STRAVA FTP</span>
        <span className={styles.strava}>{`${strava} W`}</span>
        {yours !== null && <span className={styles.yours}>{`· YOURS ${yours} W`}</span>}
      </p>
      <div className={styles.actions}>
        <button type="button" className={`btn btn-pink ${styles.action}`} disabled={busy} onClick={() => accept.mutate()}>USE IT</button>
        <button type="button" className={styles.dismiss} disabled={busy} onClick={() => dismiss.mutate(strava)}>DISMISS</button>
      </div>
      <ActionError error={accept.error ?? dismiss.error} />
    </div>
  );
}

export function FtpSuggestion() {
  const ftp = useThresholdSuggestions().data?.ftp;
  if (ftp?.state === 'reconnect') return <StravaReconnect />;
  if (ftp?.state === 'suggest') return <StravaFtpOffer strava={ftp.strava} yours={ftp.yours} />;
  return null;
}

function StepRow({ step, ticked, onToggle }: { step: MaxHrStep; ticked: boolean; onToggle: () => void }) {
  return (
    <li>
      <label className={styles.step} data-ticked={ticked}>
        <input type="checkbox" className="sr-only" checked={ticked} onChange={onToggle} />
        <span className={styles.box} aria-hidden="true">{ticked && <Check size={12} />}</span>
        <span className={styles.stepMain}>
          <span className={styles.stepDate}>{formatDayMonthYear(step.localDate)}</span>
          <span className={styles.stepRide}>{step.name}</span>
        </span>
        <span className={styles.stepValue}>{`${step.value} BPM`}</span>
      </label>
    </li>
  );
}

function MaxHrOffer({ steps }: { steps: MaxHrStep[] }) {
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const accept = useAcceptMaxHrSteps();
  const dismiss = useDismissMaxHrSuggestion();
  const ticked = steps.filter((s) => !unticked.has(s.activityId));
  const busy = accept.isPending || dismiss.isPending;
  const titleId = useId();
  return (
    <section className={styles.maxHr} aria-labelledby={titleId}>
      <h3 id={titleId} className={styles.maxHrHead}>SUGGESTED FROM YOUR RIDES</h3>
      <p className={styles.maxHrSub}>EACH RIDE THAT SET A NEW HIGHEST MAX HR · UNTICK A SENSOR SPIKE</p>
      <ul className={styles.steps}>
        {steps.map((s) => (
          <StepRow key={s.activityId} step={s} ticked={!unticked.has(s.activityId)} onToggle={() => setUnticked((prev) => toggled(prev, s.activityId))} />
        ))}
      </ul>
      <div className={styles.actions}>
        <button type="button" className={`btn btn-pink ${styles.action}`} disabled={busy || ticked.length === 0} onClick={() => accept.mutate(ticked.map((s) => s.activityId))}>
          {acceptStepsLabel(ticked.length)}
        </button>
        <button type="button" className={styles.dismiss} disabled={busy} onClick={() => dismiss.mutate(steps.map((s) => s.activityId))}>DISMISS</button>
      </div>
      <ActionError error={accept.error ?? dismiss.error} />
    </section>
  );
}

export function MaxHrSuggestion() {
  const steps = useThresholdSuggestions().data?.maxHr ?? [];
  // Keyed by the set, so a new set starts with every step ticked again.
  return steps.length === 0 ? null : <MaxHrOffer key={steps.map((s) => s.activityId).join()} steps={steps} />;
}
